import { useMemo } from "react";
import { Link } from "react-router-dom";

import { WidgetsCategoryModel } from "@interfaces/index";
import { useCommonStore, useUserStore } from "@stores/index";
import { formatAmount } from "@utils/index";
import {
  Drawer,
  DrawerHeader,
  DrawerScrollableContainer,
  ArrowUpRightSvg,
} from "@components/index";

export const WidgetCategoryDetails = ({
  widget,
  businessId,
  startDate,
  endDate,
  onClose,
}: {
  widget: WidgetsCategoryModel;
  businessId: string;
  startDate: string;
  endDate: string;
  onClose: () => void;
}) => {
  const setCommonState = useCommonStore(({ setState }) => setState);
  const puEnabled = useUserStore(({ puEnabled }) => puEnabled);
  const businesses = useUserStore(({ businesses }) => businesses);
  const disabledFeatures = useUserStore(
    ({ disabledFeatures }) => disabledFeatures,
  );

  const businessName = useMemo(
    () =>
      businesses?.find(({ uniqueId }) => uniqueId === businessId)?.name ?? "",
    [businessId, businesses],
  );

  const motorCategoryItems = useMemo(() => {
    if (
      disabledFeatures.includes("Assets") &&
      ["Motor", "Rent Expenses"].includes(widget.title)
    ) {
      const allItems = [] as (typeof widget.assets)[0]["categories"];
      widget.assets.forEach((asset) =>
        asset.categories.forEach((category) => allItems.push(category)),
      );
      const items = allItems.reduce(
        (acc, category) => {
          const existingCategory = acc.find(
            (c) => c.uniqueId === category.uniqueId,
          );
          if (existingCategory) {
            existingCategory.totalTransactionsAmount = (
              parseFloat(existingCategory.totalTransactionsAmount) +
              parseFloat(category.totalTransactionsAmount)
            ).toString();
          } else {
            acc.push({ ...category });
          }
          return acc;
        },
        [] as (typeof widget.assets)[0]["categories"],
      );

      return items;
    }

    return [];
  }, [disabledFeatures, widget]);

  const getLink = (categoryId: string, assetId: string = "") =>
    `/transactions?${new URLSearchParams({
      businessId,
      categoryId,
      assetId,
      type: widget.type,
      startDate: startDate ?? "",
      endDate: endDate ?? "",
    }).toString()}`;

  const handleClose = () => {
    setCommonState({ isDrawerShown: false });
    onClose();
  };

  return (
    <Drawer>
      <DrawerHeader
        onClose={handleClose}
        title={
          <div className="flex flex-col items-center md:items-start">
            <div className="text-300 font-bold text-black-100 md:text-600">
              {widget.title}
            </div>
            {businessName && (
              <div className="text-black-54 md:text-300 md:font-bold">
                {businessName}
              </div>
            )}
          </div>
        }
      />
      <DrawerScrollableContainer>
        <div className="flex flex-col pt-6 md:flex-row md:pt-14">
          <Link
            to={getLink(widget.uniqueId)}
            className="z-[1] grid flex-[6] gap-6 rounded-lg bg-secondary-100 pb-4 pl-4 pr-4 pt-3.5 transition-opacity hover:opacity-80 md:pl-5"
          >
            <div className="flex items-center justify-between">
              <p className="font-bold text-white-100">Total:</p>
              <span className="text-white-54 *:size-6">
                <ArrowUpRightSvg />
              </span>
            </div>
            <p className="text-400 font-bold text-white-100">
              {formatAmount(widget.totalTransactionsAmount || 0, "£")}
            </p>
          </Link>
          {puEnabled &&
            (widget.business !== undefined || widget.private !== undefined) && (
              <div className="-mt-4 flex grow-[5] gap-0.5 md:-ml-3 md:mt-0">
                {widget.business !== undefined && (
                  <div className="grid flex-1 gap-6 rounded-bl-lg bg-primary-108 p-4 pt-8 last:rounded-br-lg md:rounded-bl-none md:pl-9 md:pt-4 md:last:rounded-tr-lg">
                    <p className="font-bold text-black-54">Business:</p>
                    <p className="text-400 font-bold">
                      {formatAmount(widget.business || 0, "£")}
                    </p>
                  </div>
                )}
                {widget.private !== undefined && (
                  <div className="grid flex-1 gap-6 rounded-br-lg bg-primary-108 p-4 pt-8 first:rounded-bl-lg md:rounded-tr-lg md:pl-6 md:pt-4 md:first:rounded-bl-none first:md:pl-9">
                    <p className="font-bold text-black-54">Private:</p>
                    <p className="text-400 font-bold">
                      {formatAmount(widget.private || 0, "£")}
                    </p>
                  </div>
                )}
              </div>
            )}
        </div>
        <div
          className="pt-10 text-300 font-bold text-black-100 md:pt-14 md:text-400"
          data-testid="widgetDetails_subcategoryLabel"
        >
          Subcategory Summary
        </div>
        <div className="flex justify-between pb-4 pt-8 text-100 text-black-54 md:pt-6">
          <div>Subcategory</div>
          <div>Total</div>
        </div>
        {!disabledFeatures.includes("Assets") &&
          ["Motor", "Rent Expenses"].includes(widget.title) &&
          widget.assets.map((asset) => (
            <div key={asset.uniqueId} className="pb-8 text-300">
              <div className="text-400 font-bold">{asset.name}</div>
              {asset.categories.map(
                ({ title, uniqueId, totalTransactionsAmount }) => (
                  <Link
                    key={uniqueId}
                    to={getLink(uniqueId, asset.uniqueId)}
                    className="group flex justify-between border-t border-t-black-16 py-4 last:border-b last:border-b-black-16"
                  >
                    <p className="text-black-70 group-hover:text-secondary-100 md:text-300 md:text-black-100">
                      {title}
                    </p>
                    <p className="flex gap-x-1.5 text-black-100 group-hover:text-secondary-100 md:text-300">
                      {formatAmount(totalTransactionsAmount, "£")}
                      <span className="text-black-54 *:size-5 group-hover:text-secondary-100 md:*:size-6">
                        <ArrowUpRightSvg />
                      </span>
                    </p>
                  </Link>
                ),
              )}
            </div>
          ))}
        {disabledFeatures.includes("Assets") &&
          ["Motor", "Rent Expenses"].includes(widget.title) &&
          motorCategoryItems.map(
            ({ title, uniqueId, totalTransactionsAmount }) => (
              <Link
                key={uniqueId}
                to={getLink(uniqueId)}
                className="group flex justify-between border-t border-t-black-16 py-4 last:border-b last:border-b-black-16"
              >
                <p className="text-black-70 group-hover:text-secondary-100 md:text-300 md:text-black-100">
                  {title}
                </p>
                <p className="flex gap-x-1.5 text-black-100 group-hover:text-secondary-100 md:text-300">
                  {formatAmount(totalTransactionsAmount, "£")}
                  <span className="text-black-54 *:size-5 group-hover:text-secondary-100 md:*:size-6">
                    <ArrowUpRightSvg />
                  </span>
                </p>
              </Link>
            ),
          )}
        {!["Motor", "Rent Expenses"].includes(widget.title) &&
          widget.assets.map((asset) =>
            asset.categories.map(
              ({ title, uniqueId, totalTransactionsAmount }) => (
                <Link
                  key={`${asset.uniqueId}---${uniqueId}`}
                  to={getLink(uniqueId)}
                  className="group flex justify-between border-t border-t-black-16 py-4 last:border-b last:border-b-black-16"
                >
                  <p className="text-black-70 group-hover:text-secondary-100 md:text-300 md:text-black-100">
                    {title}
                  </p>
                  <p className="flex gap-x-1.5 text-black-100 group-hover:text-secondary-100 md:text-300">
                    {formatAmount(totalTransactionsAmount, "£")}
                    <span className="text-black-54 *:size-5 group-hover:text-secondary-100 md:*:size-6">
                      <ArrowUpRightSvg />
                    </span>
                  </p>
                </Link>
              ),
            ),
          )}
      </DrawerScrollableContainer>
    </Drawer>
  );
};
